/* Libraries */
import React, { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { history } from '~/src/utils/history';
import Button from '~/src/components/Button';

/* Containers */
import LibraryContainer from '~/src/pages/Library/Library';
import TemplatesContainer from '~/src/containers/Templates';
import TemplateSetsContainer from '~/src/containers/TemplateSets';
import CourtFormsLandingContainer, {
  stateCodeToStateName,
} from '~/src/containers/CourtFormsLandingContainer';
import PdfTableContainer from '~/src/containers/PdfTableContainer';
import StateRegionTableContainer from '~/src/containers/StateRegionTableContainer';

import { denormalizeLocality } from '~/src/components/CourtForms/StateRegionTable';
import FederalFormsWithLocalitiesContainer, {
  GENERAL_FEDERAL_FORMS_CATEGORY,
} from '~/src/containers/FederalFormsWithLocalitiesContainer';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import TemplateDefaultValuesListContainer from '~/src/containers/TemplateDefaultValuesList';
import Page, { LoadingOverlay } from '~/src/components/PageLayout';
import Tabs from '~/src/components/PageLayout/Tabs';
import { SelectionSidebarHeader } from '~/src/components/SelectionSidebar/SelectionSidebarHeader';
import {
  tryConvertToNumber,
  useCreateQuestionnaireTemplate,
  useCreateDocumentFields,
} from '~/src/entities/questionnaires';
import useMst from '~/src/hooks/useMst';
import { useGetQuestionnaireTemplates } from '~/src/entities/questionnaires/hooks/useGetQuestionnaireTemplates';
import { useLayoutContext } from '~/src/contexts';
import { LAYOUT_TOAST_TYPES } from '~/src/components/PageLayout/Toasts';
import {
  useCurrentOrgFprint,
  useDraftingAgentEnabled,
} from '~/src/entities/user';
import analyticsService from '~/src/services/analytics';
import { PdfCourtForm } from '~/src/components/CourtForms/PdfTableWrapper';
import usePdfCourtFormsActions from '~/src/hooks/usePdfCourtFormsActions';
import { Template as WordTemplate } from '~/src/components/Templates/TemplatesWrapper';
import useTemplatesActions from '~/src/hooks/useTemplatesActions';
import { useSnackbar } from 'notistack';
import { useUIFlagEnabled } from '~/src/entities/uiFlags';
import { SelectDocumentsBanner, AIDraftingBanner } from './Banner';
import {
  isSelectDocumentsBannerViewed,
  setSelectDocumentsBannerViewed,
} from './Banner/utils';
import RegionalCourtFormContainer from '~/src/containers/RegionalCourtFormContainer/RegionalCourtFormContainer';
import CourtFormsSearchable from '~/src/components/CourtForms/CourtFormsSearchable';
import { getIsRegionalPdfSupported } from '~/src/containers/RegionalCourtFormContainer/helpers';
import { Region } from '~/src/regions/config/utils';
import { TemplateSource } from '~/src/types/template';
import useShowCourtFormsTabs from '~/src/hooks/useShowCourtFormsTabs';
import { regional } from '~/src/regions/translations/regionalTranslations';

type Template = {
  defaultValueId: null | number;
  globalTemplateId: null | number;
  id: number;
  sortOrder: number;
  subtitle: string;
  title: string;
};

export const ClioQuestionnaireSelectDocumentsPage = ({
  questionnaireId,
}: {
  questionnaireId: string;
}) => {
  const showCourtFormsTabs = useShowCourtFormsTabs();

  const tabs = (
    <Tabs
      tabsMetadata={[
        {
          headerText: 'Sets',
          toUrl: `/questionnaires/${questionnaireId}/select-documents/template-sets`,
        },
        {
          headerText: regional('courtForms'),
          toUrl: `/questionnaires/${questionnaireId}/select-documents/court-forms`,
          show: showCourtFormsTabs,
        },
        {
          headerText: 'Word templates',
          toUrl: `/questionnaires/${questionnaireId}/select-documents/templates`,
        },
      ]}
      active={undefined}
      setActive={undefined}
    />
  );
  const snackbar = useSnackbar();
  const { initialize, addDocumentToSelectionSidebar } = useMst((store) => ({
    initialize: store.sidebarItems.initialize,
    addDocumentToSelectionSidebar:
      store.documents.addDocumentToSelectionSidebar,
  }));

  const { previewTemplate: handlePreviewPdfTemplate } =
    usePdfCourtFormsActions();
  const { previewTemplate: handlePreviewWordTemplate } = useTemplatesActions();

  const orgFprint = useCurrentOrgFprint();
  const createQuestionnaireTemplate = useCreateQuestionnaireTemplate();
  const getQuestionnaireTemplates = useGetQuestionnaireTemplates(
    orgFprint,
    questionnaireId,
  );
  const getDocumentFields = useCreateDocumentFields(orgFprint);
  const { showToast } = useLayoutContext();
  const isNewDocumentSelectionPanel = useUIFlagEnabled(
    'newDocumentSelectionPanel',
  );
  const isSelectDocumentsBanner = useUIFlagEnabled(
    'questionnaireSelectDocumentsBanner',
  );
  const isDraftingAgentEnabled = useDraftingAgentEnabled();
  const { isFederalFormsCategoriesEnabled } = useFeatureAccesses();

  const hasBannerBeenShown = isSelectDocumentsBannerViewed();

  const [isBannerOpen, setIsBannerOpen] = React.useState(
    isSelectDocumentsBanner && !hasBannerBeenShown,
  );
  const [isBuildingQuestionnaire, setIsBuildingQuestionnaire] =
    React.useState(false);

  const handleOnContinueClick = async (templates: Template[]) => {
    if (!getQuestionnaireTemplates.data) return;

    const templateIds = templates.map((doc) => doc.id);

    setIsBuildingQuestionnaire(true);
    try {
      const documents = await getDocumentFields.mutateAsync({
        templateIds: templateIds.map((template) => String(template)),
      });

      const documentFormats = documents.map((doc) =>
        doc.preview_pdf_url ? 'pdf' : 'docx',
      );

      // TODO: Once premium content template is released update this.
      // We will need to update BE to specify the type of document.
      const documentTypes = documents.map((doc) =>
        doc.preview_pdf_url ? 'court forms' : 'word templates',
      );

      analyticsService.track('Selected Documents', {
        questionnaire_id: tryConvertToNumber(questionnaireId),
        template_ids: documents.map((doc) => doc.id),
        document_formats: documentFormats,
        document_types: documentTypes,
      });
    } catch (e) {
      // do nothing since it's just analytics...
    }

    createQuestionnaireTemplate.mutateAsync(
      {
        orgFprint,
        questionnaireId,
        templateIds,
      },
      {
        onError: () => {
          showToast(LAYOUT_TOAST_TYPES.error, {
            message: 'Something went wrong, please try again later.',
          });
          setIsBuildingQuestionnaire(false);
        },
        onSuccess: () => {
          history.push(`/questionnaires/${questionnaireId}/auto-generate`);
        },
      },
    );
  };

  useEffect(() => {
    if (
      !getQuestionnaireTemplates.isLoading &&
      getQuestionnaireTemplates.data
    ) {
      getQuestionnaireTemplates.data.forEach((template) => {
        addDocumentToSelectionSidebar({
          id: template.template_id,
          title: `${template.template.title}`,
          subtitle: `${template.template.subtitle}`,
        });
      });
    }

    return () => {
      initialize();
    };
  }, [
    addDocumentToSelectionSidebar,
    getQuestionnaireTemplates.data,
    getQuestionnaireTemplates.isLoading,
    initialize,
  ]);

  const onPreviewPdfTemplate = (template: PdfCourtForm) => {
    analyticsService.track('Selected Documents', {
      questionnaire_id: tryConvertToNumber(questionnaireId),
      template_id: tryConvertToNumber(template.id),
      document_format: 'pdf',
      document_type: 'court forms',
    });
    handlePreviewPdfTemplate(template);
  };

  const onPreviewDocxTemplate = (template: WordTemplate) => {
    analyticsService.track('Selected Documents', {
      questionnaire_id: tryConvertToNumber(questionnaireId),
      template_id: tryConvertToNumber(template.id),
      document_format: 'docx',
      document_type: 'word templates',
    });
    handlePreviewWordTemplate(template);
  };

  const handleBannerClose = () => {
    setSelectDocumentsBannerViewed();
    setIsBannerOpen(false);
  };

  if (
    createQuestionnaireTemplate.isError ||
    getQuestionnaireTemplates.isError
  ) {
    snackbar.enqueueSnackbar(
      'Failed to create questionnaire. If this issue persists, contact our support team.',
      {
        variant: 'error',
      },
    );
    history.push(`/questionnaires/`);

    return <></>;
  }

  if (
    createQuestionnaireTemplate.isLoading ||
    getQuestionnaireTemplates.isLoading
  )
    return (
      <LoadingOverlay title="Loading document selection..." relative={false} />
    );

  const isRegionalPdfSupported = getIsRegionalPdfSupported();

  return (
    <Page
      showSelectionSidebar
      showAvatar={false}
      showNavigation={false}
      title={'Select documents to build the questionnaire'}
      headerProps={{
        tabs,
        showCloseButton: true,
        onClose: () => {
          history.replace('/questionnaires');
        },
      }}
      selectionSidebarProps={{
        renderHeader: (items: string | any[]) => (
          <SelectionSidebarHeader
            title={
              isNewDocumentSelectionPanel ? 'Documents selected' : 'Selection'
            }
            count={items.length}
            useAlphaFeedbackStyle={isNewDocumentSelectionPanel}
          />
        ),
        renderFooter: (items: any[]) => (
          <div className="border-t p-3 flex flex-row-reverse bg-white">
            <Button
              primary
              disabled={items.length === 0 || isBuildingQuestionnaire}
              onClick={() => handleOnContinueClick(items)}
            >
              {isBuildingQuestionnaire
                ? 'Processing...'
                : 'Build questionnaire'}
            </Button>
          </div>
        ),
      }}
    >
      {isDraftingAgentEnabled ? (
        <AIDraftingBanner
          open={isBannerOpen}
          questionnaireId={questionnaireId}
          onClose={handleBannerClose}
        />
      ) : (
        <SelectDocumentsBanner
          open={isBannerOpen}
          questionnaireId={questionnaireId}
          onClose={handleBannerClose}
        />
      )}
      <LibraryContainer>
        <Switch>
          <Redirect
            exact
            from="/questionnaires/:questionnaireId/select-documents"
            to="/questionnaires/:questionnaireId/select-documents/template-sets"
          />

          <Route
            exact
            path="/questionnaires/:questionnaireId/select-documents/template-sets"
          >
            <TemplateSetsContainer />
          </Route>

          {showCourtFormsTabs && (
            <Route
              exact
              path="/questionnaires/:questionnaireId/select-documents/court-forms"
            >
              <CourtFormsSearchable>
                {isRegionalPdfSupported ? (
                  <RegionalCourtFormContainer
                    routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                  />
                ) : (
                  <CourtFormsLandingContainer
                    routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                  />
                )}
              </CourtFormsSearchable>
            </Route>
          )}
          {showCourtFormsTabs && (
            <Route
              path="/questionnaires/:questionnaireId/select-documents/court-forms/additional-region-forms/:regionCode"
              render={({ match: { url, params } }) => (
                <RegionalCourtFormContainer
                  routeUrl={url}
                  regionFilter={(params as { regionCode: Region }).regionCode}
                  courtFormsRootUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                />
              )}
            />
          )}
          {showCourtFormsTabs && (
            <Route
              exact
              path="/questionnaires/:questionnaireId/select-documents/court-forms/federal_USCIS"
            >
              {isFederalFormsCategoriesEnabled ? (
                <FederalFormsWithLocalitiesContainer
                  routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                />
              ) : (
                <PdfTableContainer
                  routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                  filterCategory="FEDERAL"
                  onPreviewTemplate={onPreviewPdfTemplate}
                />
              )}
            </Route>
          )}
          {showCourtFormsTabs && (
            <Route
              exact
              path="/questionnaires/:questionnaireId/select-documents/court-forms/:stateCode/form-listing"
              render={({ match: { params } }) => {
                const { stateCode } = params;
                return (
                  <PdfTableContainer
                    routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                    filterCategory={
                      stateCode as keyof typeof stateCodeToStateName
                    }
                    locality="None"
                    onPreviewTemplate={onPreviewPdfTemplate}
                  />
                );
              }}
            />
          )}
          {showCourtFormsTabs && (
            <Route
              exact
              path="/questionnaires/:questionnaireId/select-documents/court-forms/:stateCode/locality-listing"
              render={({ match: { params } }) => {
                const { stateCode } = params;
                return (
                  <StateRegionTableContainer
                    routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                    filterCategory={stateCode}
                  />
                );
              }}
            />
          )}
          {showCourtFormsTabs && (
            <Route
              exact
              path="/questionnaires/:questionnaireId/select-documents/court-forms/:stateCode/:locality/form-listing"
              render={({ match: { params } }) => {
                const { stateCode, locality: normalizedLocality } = params;
                const denormalizedLocality =
                  denormalizeLocality(normalizedLocality);
                const effectiveLocality =
                  denormalizedLocality === GENERAL_FEDERAL_FORMS_CATEGORY
                    ? 'None'
                    : denormalizedLocality;
                return (
                  <PdfTableContainer
                    routeUrl={`/questionnaires/${questionnaireId}/select-documents/court-forms`}
                    filterCategory={
                      stateCode as keyof typeof stateCodeToStateName
                    }
                    locality={effectiveLocality}
                    onPreviewTemplate={onPreviewPdfTemplate}
                  />
                );
              }}
            />
          )}
          {!showCourtFormsTabs && (
            <Redirect
              from="/questionnaires/:questionnaireId/select-documents/court-forms"
              to="/questionnaires/:questionnaireId/select-documents/template-sets"
            />
          )}

          <Route
            exact
            path="/questionnaires/:questionnaireId/select-documents/form-templates"
          >
            <TemplateDefaultValuesListContainer />
          </Route>

          <Route
            exact
            path="/questionnaires/:questionnaireId/select-documents/templates/"
          >
            <TemplatesContainer onPreviewTemplate={onPreviewDocxTemplate} />
          </Route>
          <Route
            exact
            path="/questionnaires/:questionnaireId/select-documents/templates/clio"
          >
            <TemplatesContainer
              onPreviewTemplate={onPreviewDocxTemplate}
              source={TemplateSource.Clio}
            />
          </Route>
        </Switch>
      </LibraryContainer>
    </Page>
  );
};
